import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { ArrowLeft, Download, Plus, Trash2, Upload } from "lucide-react";
import StatusBadge from "./StatusBadge";
import { FieldError, inputClass } from "./SuggestionEditor";
import { getTemplate, isAgentNotConfigured, sendMessages } from "./rcsTemplateApi";
import {
  MAX_VALUE_LENGTH,
  fieldLabel,
  recipientsFromSheet,
  rowsToRecipients,
  sampleHeaders,
  sheetColumn,
} from "./templatePayload";

const CHUNK_SIZE = 1000;
const RCS_HISTORY_PATH = "/rcstransaction";

const emptyRow = (columns) => Object.fromEntries(columns.map((c) => [c, ""]));

// Reads the first sheet. Mobile numbers are taken from the raw cell value so
// long numbers aren't mangled by Excel's display format (e.g. 9.19877E+11).
const parseSheet = async (file) => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];
  const formatted = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
  const raw = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
  return formatted.map((row, i) => {
    const merged = { ...row };
    const rawMobile = raw[i]?.mobile;
    if (typeof rawMobile === "number") merged.mobile = String(rawMobile);
    Object.defineProperty(merged, "__rowNum__", { value: row.__rowNum__, enumerable: false });
    return merged;
  });
};

// Maps "recipients[3]: invalid mobile number" to the row it came from.
const mapSendErrors = (errors, offset, rowNumbers, rowWord) =>
  [].concat(errors || []).map((entry) => {
    const text = String(entry);
    const match = text.match(/^recipients\[(\d+)\]:?\s*(.*)$/);
    if (!match) return text;
    const index = offset + Number(match[1]);
    const row = rowNumbers[index];
    return row ? `${rowWord} ${row}: ${match[2]}` : text;
  });

const SendTemplate = ({ nav, onAgentMissing, id }) => {
  const [template, setTemplate] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [campaign, setCampaign] = useState("");
  const [mode, setMode] = useState("manual");
  const [rows, setRows] = useState([]);
  const [attempted, setAttempted] = useState(false);
  const [upload, setUpload] = useState(null); // {fileName, recipients, errors, rowNumbers}
  const [progress, setProgress] = useState(null); // {sent, total}
  const [result, setResult] = useState(null); // {queued, errors?, message?}

  const variableFields = useMemo(() => template?.variable_fields || [], [template]);
  const columns = useMemo(() => sampleHeaders(variableFields), [variableFields]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getTemplate(id);
      if (cancelled) return;
      if (res.ok) {
        setTemplate(res.data.data);
      } else if (isAgentNotConfigured(res)) {
        onAgentMissing();
      } else if (res.status === 404) {
        toast.error("Template not found");
        nav.list();
      } else {
        setLoadError(res.error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, onAgentMissing]);

  useEffect(() => {
    if (template) setRows([emptyRow(columns)]);
  }, [template, columns]);

  const manual = useMemo(
    () => rowsToRecipients(rows, variableFields, (i) => i + 1),
    [rows, variableFields]
  );
  const manualErrorsByIndex = useMemo(
    () => Object.fromEntries(manual.errors.map((e) => [e.index, e.messages])),
    [manual]
  );

  const sending = progress !== null && result === null;

  const updateCell = (index, column, value) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [column]: value } : r)));

  const downloadSample = () => {
    const sheet = XLSX.utils.aoa_to_sheet([columns]);
    sheet["!cols"] = columns.map((c) => ({ wch: Math.max(14, c.length + 2) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Recipients");
    XLSX.writeFile(workbook, `rcs_template_${template.id}_sample.xlsx`);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setResult(null);
    try {
      const sheetRows = await parseSheet(file);
      const missing = columns.filter((c) => sheetRows.length && !(c in sheetRows[0]));
      const parsed = recipientsFromSheet(sheetRows, variableFields);
      if (missing.length) {
        parsed.errors.unshift({ row: 1, messages: [`missing column(s): ${missing.join(", ")}`] });
      }
      if (!sheetRows.length) parsed.errors.push({ row: "—", messages: ["the sheet has no rows"] });
      setUpload({ fileName: file.name, ...parsed });
    } catch (err) {
      setUpload({ fileName: file.name, recipients: [], rowNumbers: [], errors: [{ row: "—", messages: ["couldn't read this file"] }] });
    }
  };

  const send = async () => {
    const source = mode === "manual" ? manual : upload;
    if (mode === "manual") setAttempted(true);
    if (!source || source.errors.length || !source.recipients.length) return;

    const { recipients, rowNumbers } = source;
    const rowWord = mode === "manual" ? "Row" : "Sheet row";
    const campaignId = campaign.trim();
    let queued = 0;
    setResult(null);
    setProgress({ sent: 0, total: recipients.length });

    for (let offset = 0; offset < recipients.length; offset += CHUNK_SIZE) {
      const body = { template_id: template.id, recipients: recipients.slice(offset, offset + CHUNK_SIZE) };
      if (campaignId) body.campaign_id = campaignId;
      const res = await sendMessages(body);

      if (res.status !== 202) {
        if (isAgentNotConfigured(res)) {
          onAgentMissing();
          return;
        }
        setResult({
          queued,
          total: recipients.length,
          message: res.error,
          errors: mapSendErrors(res.errors, offset, rowNumbers, rowWord),
        });
        return;
      }
      queued += (res.data.data || []).length || body.recipients.length;
      setProgress({ sent: queued, total: recipients.length });
    }
    setResult({ queued, total: recipients.length });
  };

  const header = (
    <div className="flex flex-wrap items-center gap-3 mb-6">
      <button
        onClick={() => (template ? nav.detail(template.id) : nav.list())}
        className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
        title="Back"
      >
        <ArrowLeft size={18} />
      </button>
      <h1 className="text-2xl font-bold text-gray-800 break-all">
        Send {template ? `“${template.template_name}”` : "template"}
      </h1>
      {template && <StatusBadge status={template.status} />}
    </div>
  );

  if (loadError) {
    return (
      <div>
        {header}
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{loadError}</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div>
        {header}
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00b6ce]" />
        </div>
      </div>
    );
  }

  if (template.status !== "approved") {
    return (
      <div>
        {header}
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 max-w-xl">
          This template isn't approved yet, so it can't be sent. You can send it once its
          status is Approved.
        </div>
      </div>
    );
  }

  const source = mode === "manual" ? manual : upload;
  const readyCount = source?.recipients.length || 0;
  const blocked = !source || source.errors.length > 0 || readyCount === 0;
  const chunks = Math.ceil(readyCount / CHUNK_SIZE);

  return (
    <div className="space-y-5">
      {header}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-5">
        <label className="block max-w-md">
          <span className="block text-sm font-medium text-gray-700 mb-1">Campaign name (optional)</span>
          <input
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            maxLength={255}
            placeholder={template.template_name}
            className={inputClass()}
          />
        </label>

        <div className="flex gap-2">
          {[
            { value: "manual", label: "Enter manually" },
            { value: "upload", label: "Upload Excel / CSV" },
          ].map((m) => (
            <button
              key={m.value}
              onClick={() => {
                setMode(m.value);
                setResult(null);
              }}
              disabled={sending}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${
                mode === m.value
                  ? "bg-[#00b6ce] text-white border-[#00b6ce]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-[#00b6ce]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {mode === "manual" && (
          <div className="space-y-3">
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="text-sm min-w-full">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th rowSpan={variableFields.length ? 2 : 1} className="px-3 py-2 text-left align-bottom w-10">
                      #
                    </th>
                    <th rowSpan={variableFields.length ? 2 : 1} className="px-3 py-2 text-left align-bottom min-w-[160px]">
                      Mobile
                    </th>
                    {variableFields.map((f) => (
                      <th key={f.path} colSpan={f.count} className="px-3 pt-2 text-left border-l border-gray-200">
                        {fieldLabel(f.path)}
                      </th>
                    ))}
                    <th rowSpan={variableFields.length ? 2 : 1} className="w-10" />
                  </tr>
                  {variableFields.length > 0 && (
                    <tr>
                      {variableFields.flatMap((f) =>
                        Array.from({ length: f.count }, (_, n) => (
                          <th
                            key={sheetColumn(f.path, n + 1)}
                            className={`px-3 pb-2 text-left font-normal text-gray-400 ${n === 0 ? "border-l border-gray-200" : ""}`}
                          >
                            {`{{${n + 1}}}`}
                          </th>
                        ))
                      )}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const rowErrors = attempted ? manualErrorsByIndex[i] : null;
                    return (
                      <React.Fragment key={i}>
                        <tr className="border-t border-gray-100">
                          <td className="px-3 py-2 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2">
                            <input
                              value={row.mobile}
                              onChange={(e) => updateCell(i, "mobile", e.target.value)}
                              placeholder="9876543210"
                              className={inputClass(rowErrors?.some((m) => m.startsWith("invalid mobile")))}
                            />
                          </td>
                          {variableFields.flatMap((f) =>
                            Array.from({ length: f.count }, (_, n) => {
                              const col = sheetColumn(f.path, n + 1);
                              const value = row[col] || "";
                              const bad = attempted && (!value.trim() || value.trim().length > MAX_VALUE_LENGTH);
                              return (
                                <td key={col} className="px-3 py-2 min-w-[140px]">
                                  <input
                                    value={value}
                                    onChange={(e) => updateCell(i, col, e.target.value)}
                                    placeholder={`${fieldLabel(f.path)} {{${n + 1}}}`}
                                    className={inputClass(bad)}
                                  />
                                </td>
                              );
                            })
                          )}
                          <td className="px-2 py-2">
                            <button
                              onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                              disabled={rows.length <= 1 || sending}
                              className="text-gray-400 hover:text-red-500 disabled:opacity-30 p-1"
                              title="Remove row"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                        {rowErrors && (
                          <tr>
                            <td />
                            <td colSpan={columns.length + 1} className="px-3 pb-2">
                              <FieldError error={rowErrors.join("; ")} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button
              onClick={() => setRows((prev) => [...prev, emptyRow(columns)])}
              disabled={sending}
              className="flex items-center gap-1 text-sm font-semibold text-[#00b6ce] hover:text-[#009baf]"
            >
              <Plus size={16} /> Add recipient
            </button>
          </div>
        )}

        {mode === "upload" && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-[#00b6ce] text-[#00b6ce] text-sm font-semibold cursor-pointer hover:bg-[#00b6ce]/5">
                <Upload size={16} />
                {upload ? "Choose another file" : "Choose .xlsx or .csv"}
                <input type="file" accept=".xlsx,.csv" onChange={onFile} disabled={sending} className="hidden" />
              </label>
              <button
                onClick={downloadSample}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50"
              >
                <Download size={16} /> Download sample
              </button>
            </div>
            <p className="text-xs text-gray-400">
              Columns: <code>{columns.join(", ")}</code>. Each value can be at most {MAX_VALUE_LENGTH}{" "}
              characters.
            </p>
            {upload && (
              <div className="text-sm">
                <div className="text-gray-700">
                  <span className="font-semibold">{upload.fileName}</span>: {upload.recipients.length} valid{" "}
                  {upload.recipients.length === 1 ? "recipient" : "recipients"}
                  {upload.errors.length > 0 && `, ${upload.errors.length} with errors`}
                </div>
                {upload.errors.length > 0 && (
                  <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 max-h-64 overflow-y-auto">
                    <div className="font-semibold mb-1">Fix these rows and upload again:</div>
                    <ul className="list-disc pl-5 space-y-0.5">
                      {upload.errors.map((e, i) => (
                        <li key={i}>
                          {typeof e.row === "number" ? `Row ${e.row}` : e.row}: {e.messages.join("; ")}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
          <button
            onClick={send}
            disabled={sending || (mode === "upload" && blocked)}
            className="bg-[#00b6ce] hover:bg-[#009baf] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg text-sm font-semibold"
          >
            {sending
              ? `Sending… ${progress.sent} of ${progress.total}`
              : readyCount > 0
              ? `Send to ${readyCount} ${readyCount === 1 ? "recipient" : "recipients"}`
              : "Send"}
          </button>
          {chunks > 1 && !sending && (
            <span className="text-xs text-gray-400">
              Sent in {chunks} batches of up to {CHUNK_SIZE}.
            </span>
          )}
          {mode === "manual" && attempted && manual.errors.length > 0 && (
            <span className="text-xs text-red-600">
              Fix {manual.errors.length} {manual.errors.length === 1 ? "row" : "rows"} before sending.
            </span>
          )}
        </div>
      </div>

      {result && !result.message && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <span className="font-semibold">
            Queued {result.queued} {result.queued === 1 ? "message" : "messages"}.
          </span>{" "}
          Track delivery in{" "}
          <Link to={RCS_HISTORY_PATH} className="underline font-semibold">
            RCS logs
          </Link>
          .
        </div>
      )}

      {result && result.message && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 space-y-2">
          <div className="font-semibold">Sending stopped: {result.message}</div>
          <div>
            {result.queued > 0
              ? `${result.queued} of ${result.total} messages were already queued before the error. The rest were not sent.`
              : "No messages were queued."}
            {result.queued > 0 && (
              <>
                {" "}
                See{" "}
                <Link to={RCS_HISTORY_PATH} className="underline font-semibold">
                  RCS logs
                </Link>
                .
              </>
            )}
          </div>
          {result.errors.length > 0 && (
            <ul className="list-disc pl-5 space-y-0.5 max-h-64 overflow-y-auto">
              {result.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SendTemplate;
